/**
 * AdministerSectionPagesV2 — Phase 10 of the Rebirth migration.
 *
 * Round 5 upgrade (G39–G44) provided first-pass real content for Team,
 * Deployments, Governed Gateway API Keys, and Governed Gateway Dashboard.
 * Static representative data is fine — when backend endpoints land, the
 * shape stays the same and only the data source swaps.
 *
 * Admin Center: the Rebirth v2 rewrite that lived here (AdminCenterPageV2Inner
 * + its per-tab components) was superseded by G88 and removed. The canonical
 * AdminCenterPage now owns the full admin surface; ResolvedAdminCenterPage
 * simply delegates to it.
 */
import React from 'react';
import { Users, KeyRound, ExternalLink } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AdminCenterPage from './AdminCenterPage';
import {
  V2PageShell, Button, Pill, Card, MetricCard, Table,
} from '../components/v2';

export function ResolvedAdminCenterPage(props) {
  /* The Admin Center is the canonical AdminCenterPage, which owns the full
   * admin surface. The Rebirth-Phase-10 v2 rewrite (AdminCenterPageV2Inner)
   * was superseded by G88 and has been removed along with its dead per-tab
   * components; this resolver now exists only to keep the route import stable. */
  return <AdminCenterPage {...props} />;
}

/* ═══════════════════════════════════════════════════════════════════
   TEAM — Users / Groups / Roles
   ─────────────────────────────────────────────────────────────────── */
function TeamPageV2Inner() {
  const navigate = useNavigate();
  return (
    <V2PageShell
      title="Team"
      subtitle="Users · Groups · Roles (RBAC matrix)"
      tags={<Pill tone="cyan" size="sm">Administer</Pill>}
      actions={<Button variant="primary">+ Invite user</Button>}
    >
      <div className="space-y-5">
        <div className="grid sm:grid-cols-4 gap-4">
          <MetricCard label="Active users"     value="38"  tone="cyan"    delta={{ value: '+2',  direction: 'up',   baseline: 'this week', intent: 'positive' }} />
          <MetricCard label="Pending invites"  value="3"   tone="warning" delta={{ value: 'oldest 4d',  direction: 'flat', intent: 'neutral' }} />
          <MetricCard label="Groups"           value="12"  tone="trust"   delta={{ value: '4 mapped to SSO',  direction: 'flat', intent: 'neutral' }} />
          <MetricCard label="Custom roles"     value="6"   tone="success" delta={{ value: '5 RBAC + 1 oncall', direction: 'flat', intent: 'neutral' }} />
        </div>
        <Card>
          <Card.Header title="Users · 38" actions={<Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => navigate('/team')}>Open Team page</Button>} />
          <Card.Body>
            <Table
              ariaLabel="Users"
              density="compact"
              columns={[
                { key: 'name',   header: 'Name',  render: (r) => <strong className="text-v2-ink">{r.name}</strong> },
                { key: 'email',  header: 'Email', render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-secondary">{r.email}</span> },
                { key: 'role',   header: 'Role',  render: (r) => <Pill tone={r.role === 'Super Admin' ? 'danger' : r.role === 'Org Admin' ? 'trust' : 'cyan'} size="sm">{r.role}</Pill> },
                { key: 'mfa',    header: 'MFA',   render: (r) => <Pill tone={r.mfa ? 'success' : 'warning'} dot size="sm">{r.mfa ? 'on' : 'off'}</Pill> },
                { key: 'last',   header: 'Last sign-in', align: 'right', render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.last}</span> },
              ]}
              rows={[
                { name: 'Platform Admin',       email: 'admin@prove7.ai',     role: 'Super Admin', mfa: true,  last: '14:08' },
                { name: 'Maya Rodriguez',       email: 'maya@prove7.ai',      role: 'Org Admin',   mfa: true,  last: '13:52' },
                { name: 'Jordan Park',          email: 'jpark@prove7.ai',     role: 'Tenant Admin', mfa: true,  last: '12:18' },
                { name: 'Vivian Chen',          email: 'vivian@prove7.ai',    role: 'Developer',   mfa: false, last: 'Yesterday' },
                { name: 'Ravi Patel',           email: 'ravi@prove7.ai',      role: 'Viewer',      mfa: true,  last: '2d ago' },
              ]}
              rowKey={(r) => r.email}
            />
          </Card.Body>
        </Card>
      </div>
    </V2PageShell>
  );
}
/* G108 — TeamPage v2 inventory pattern (Users + Groups full v2,
 * other tabs link to v1 for full functionality). */
import { default as TeamPageV2Resolver } from './TeamPageV2';
export function ResolvedTeamPage(props) {
  return <TeamPageV2Resolver {...props} />;
}

/* ═══════════════════════════════════════════════════════════════════
   DEPLOYMENTS — Org / Tenant tree
   ─────────────────────────────────────────────────────────────────── */
function DeploymentsPageV2Inner() {
  const navigate = useNavigate();
  return (
    <V2PageShell
      title="Deployments"
      subtitle="Org / Tenant hierarchy · deployment tracking"
      tags={<Pill tone="cyan" size="sm">Administer</Pill>}
      actions={<Button variant="primary">+ Deployment</Button>}
    >
      <div className="space-y-5">
        <div className="grid sm:grid-cols-4 gap-4">
          <MetricCard label="Organizations"   value="3" tone="trust"   delta={{ value: 'Acme · Globex · Initech', direction: 'flat', intent: 'neutral' }} />
          <MetricCard label="Tenants"         value="12" tone="cyan"   delta={{ value: '+1 this month', direction: 'up', intent: 'positive' }} />
          <MetricCard label="Live deployments" value="9" tone="success" delta={{ value: '0 degraded', direction: 'flat', intent: 'positive' }} />
          <MetricCard label="CPU / mem avg"   value="42% / 58%" tone="warning" delta={{ value: 'within budget', direction: 'flat', intent: 'neutral' }} />
        </div>
        <Card>
          <Card.Header title="Active deployments" actions={<Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => navigate('/deployments')}>Open Deployments page</Button>} />
          <Card.Body>
            <Table
              ariaLabel="Active deployments"
              density="compact"
              columns={[
                { key: 'org',     header: 'Org',     render: (r) => <strong className="text-v2-ink">{r.org}</strong> },
                { key: 'tenant',  header: 'Tenant',  render: (r) => <span>{r.tenant}</span> },
                { key: 'env',     header: 'Env',     render: (r) => <Pill tone={r.env === 'prod' ? 'danger' : r.env === 'staging' ? 'warning' : 'cyan'} size="sm">{r.env}</Pill> },
                { key: 'status',  header: 'Status',  render: (r) => <Pill tone={r.status === 'healthy' ? 'success' : 'warning'} dot size="sm">{r.status}</Pill> },
                { key: 'cpu',     header: 'CPU',     render: (r) => <span className="tabular-nums">{r.cpu}%</span> },
                { key: 'mem',     header: 'Mem',     render: (r) => <span className="tabular-nums">{r.mem}%</span> },
              ]}
              rows={[
                { org: 'Acme',    tenant: 'acme-prod',     env: 'prod',    status: 'healthy', cpu: 38, mem: 54 },
                { org: 'Acme',    tenant: 'acme-staging',  env: 'staging', status: 'healthy', cpu: 22, mem: 41 },
                { org: 'Globex',  tenant: 'globex-prod',   env: 'prod',    status: 'healthy', cpu: 45, mem: 62 },
                { org: 'Initech', tenant: 'initech-prod',  env: 'prod',    status: 'healthy', cpu: 51, mem: 68 },
                { org: 'Initech', tenant: 'initech-dev',   env: 'dev',     status: 'healthy', cpu: 18, mem: 33 },
              ]}
              rowKey={(r) => `${r.org}-${r.tenant}`}
            />
          </Card.Body>
        </Card>
      </div>
    </V2PageShell>
  );
}
/* G114 — proper v2 inventory pattern. */
import ResolvedDeploymentsPageV2 from './DeploymentsPageV2';
export function ResolvedDeploymentsPage(props) {
  return <ResolvedDeploymentsPageV2 {...props} />;
}

/* ═══════════════════════════════════════════════════════════════════
   GATEWAY · API KEYS
   ─────────────────────────────────────────────────────────────────── */
function GatewayApiKeysPageV2Inner() {
  const navigate = useNavigate();
  return (
    <V2PageShell
      title="Protected Gateway · API Keys"
      subtitle="Generate · rotate · revoke · renew · per-key rate limits"
      tags={<Pill tone="cyan" size="sm">Administer</Pill>}
      backTo="/gateway-dashboard"
      backLabel="Gateway dashboard"
      actions={<Button variant="primary" iconLeft={<KeyRound />}>Generate API key</Button>}
    >
      <div className="space-y-5">
        <div className="grid sm:grid-cols-4 gap-4">
          <MetricCard label="Active keys"      value="24"  tone="cyan"    delta={{ value: '+2 this week', direction: 'up', intent: 'neutral' }} />
          <MetricCard label="Expiring ≤ 30d"   value="3"   tone="warning" delta={{ value: 'oldest in 8d', direction: 'flat', intent: 'negative' }} />
          <MetricCard label="Revoked / 90d"    value="5"   tone="muted"   delta={{ value: '2 by rotation policy', direction: 'flat', intent: 'neutral' }} />
          <MetricCard label="Rate-limit hits"  value="12"  tone="warning" delta={{ value: 'across 2 keys', direction: 'flat', intent: 'negative' }} />
        </div>
        <Card>
          <Card.Header title="API keys" actions={<Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => navigate('/gateway-keys')}>Open API keys</Button>} />
          <Card.Body>
            <Table
              ariaLabel="API keys"
              density="compact"
              columns={[
                { key: 'name',    header: 'Name',    render: (r) => <strong className="text-v2-ink">{r.name}</strong> },
                { key: 'scope',   header: 'Scope',   render: (r) => <Pill tone="muted" size="sm">{r.scope}</Pill> },
                { key: 'created', header: 'Created', render: (r) => <span className="font-mono-v2 text-[11px] text-v2-ink-muted">{r.created}</span> },
                { key: 'expires', header: 'Expires', render: (r) => <span className={'font-mono-v2 text-[11px] ' + (r.expiresSoon ? 'text-v2-warning font-semibold' : 'text-v2-ink-muted')}>{r.expires}</span> },
                { key: 'rpm',     header: 'Limit',   render: (r) => <span className="tabular-nums">{r.rpm} RPM</span> },
                { key: 'status',  header: 'Status',  render: (r) => <Pill tone={r.status === 'active' ? 'success' : r.status === 'expiring' ? 'warning' : 'muted'} dot size="sm">{r.status}</Pill> },
              ]}
              rows={[
                { name: 'sophie-prod',     scope: 'sophie:execute',     created: '2025-12-10', expires: '2026-12-10', expiresSoon: false, rpm: 240, status: 'active' },
                { name: 'aria-soc-readonly', scope: 'aria:read',        created: '2025-09-15', expires: '2026-05-17', expiresSoon: true,  rpm:  60, status: 'expiring' },
                { name: 'echo-evidence',   scope: 'echo:evidence',      created: '2026-01-22', expires: '2027-01-22', expiresSoon: false, rpm: 120, status: 'active' },
                { name: 'tenant-init-dev', scope: 'tenant:dev:*',       created: '2026-04-01', expires: '2026-07-01', expiresSoon: false, rpm:  30, status: 'active' },
              ]}
              rowKey={(r) => r.name}
            />
          </Card.Body>
        </Card>
      </div>
    </V2PageShell>
  );
}
/* G115 — proper v2 inventory. */
import ResolvedGatewayApiKeysPageV2 from './GatewayApiKeysPageV2';
export function ResolvedGatewayApiKeysPage(props) {
  return <ResolvedGatewayApiKeysPageV2 {...props} />;
}

/* ═══════════════════════════════════════════════════════════════════
   GATEWAY · DASHBOARD
   ─────────────────────────────────────────────────────────────────── */
function GatewayDashboardPageV2Inner() {
  const navigate = useNavigate();
  return (
    <V2PageShell
      title="Protected Gateway · Dashboard"
      subtitle="Request volume · auth success · rate-limit hits · top endpoints · error heatmap"
      tags={<Pill tone="cyan" size="sm">Administer</Pill>}
    >
      <div className="space-y-5">
        <div className="grid sm:grid-cols-4 gap-4">
          <MetricCard label="Requests / 24h"  value="48,219" tone="cyan"    delta={{ value: '+8%', direction: 'up', baseline: 'vs. yesterday', intent: 'neutral' }} trend={[5400, 5800, 6100, 6400, 6200, 6500, 6800]} />
          <MetricCard label="Auth success"    value="99.6%"  tone="success" delta={{ value: '−0.1pp', direction: 'down', intent: 'neutral' }} />
          <MetricCard label="Rate-limit hits" value="12"     tone="warning" delta={{ value: '2 keys',  direction: 'flat', intent: 'negative' }} />
          <MetricCard label="P95 latency"     value="48 ms"  tone="trust"   delta={{ value: '−6 ms',  direction: 'down', intent: 'positive' }} />
        </div>
        <Card>
          <Card.Header title="Top endpoints · 24h" actions={<Button variant="secondary" size="sm" iconLeft={<ExternalLink />} onClick={() => navigate('/gateway-dashboard')}>Open in v1</Button>} />
          <Card.Body>
            <Table
              ariaLabel="Top gateway endpoints"
              density="compact"
              columns={[
                { key: 'endpoint', header: 'Endpoint',     render: (r) => <code className="font-mono-v2 text-[11.5px] text-v2-ink-secondary">{r.endpoint}</code> },
                { key: 'reqs',     header: 'Reqs',         render: (r) => <span className="tabular-nums font-semibold">{r.reqs.toLocaleString()}</span> },
                { key: 'p95',      header: 'P95 ms',       render: (r) => <span className="tabular-nums">{r.p95}</span> },
                { key: 'errors',   header: 'Errors',       render: (r) => <span className={'tabular-nums ' + (r.errors > 0 ? 'text-v2-warning font-semibold' : 'text-v2-ink-muted')}>{r.errors}</span> },
              ]}
              rows={[
                { endpoint: 'POST /v1/agents/{id}/execute', reqs: 18412, p95:  72, errors: 4 },
                { endpoint: 'GET  /v1/agents',              reqs:  9824, p95:  18, errors: 0 },
                { endpoint: 'POST /v1/skills/{id}/eval',    reqs:  4218, p95: 142, errors: 1 },
                { endpoint: 'GET  /v1/audit',               reqs:  2811, p95:  28, errors: 0 },
                { endpoint: 'POST /v1/policies/{id}/test',  reqs:  1102, p95:  64, errors: 2 },
              ]}
              rowKey={(r) => r.endpoint}
            />
          </Card.Body>
        </Card>
      </div>
    </V2PageShell>
  );
}
/* G120 — proper v2 dashboard. */
import ResolvedGatewayDashboardPageV2 from './GatewayDashboardPageV2';
export function ResolvedGatewayDashboardPage(props) {
  return <ResolvedGatewayDashboardPageV2 {...props} />;
}
